import { computed } from 'vue';

import { useCurrentCalculator } from '@/composables/useCurrentCalculator';
import { SafeNavigation } from '@/utils/safeNavigation';

export function useCurrentCalculatorSegments() {
  const { currentCalculator } = useCurrentCalculator();

  const currentSegments = computed(() => {
    if (!currentCalculator.value) {
      return null;
    }

    if (!currentCalculator) {
      throw Error('Nie znaleziono kalkulatora');
    }

    return currentCalculator.value.segments;
  });

  const unrequiredSegments = computed(() => {
    return currentSegments.value?.filter((segment) => !segment.is_required);
  });

  const requiredSegments = computed(() => {
    return currentSegments.value?.filter((segment) => !!segment.is_required);
  });

  const validRequiredSegments = computed(() => {
    return requiredSegments.value?.filter((segment) =>
      SafeNavigation.validateSegmentForNavigation(segment)
    );
  });

  function getSegmentById(id: number) {
    return currentSegments.value?.find((segment) => segment.id === id) || null;
  }

  function getSegmentNameById(id: number) {
    const segment = getSegmentById(id);

    if (!segment?.name) {
      return null;
    }

    if (segment.name.length > 34) {
      return `${segment.name.slice(0, 34)}...`;
    }

    return segment.name;
  }

  function getSegmentFirstViewId(id: number) {
    const segment = getSegmentById(id);

    if (!segment?.view_groups?.length) {
      return null;
    }

    const views = segment.view_groups.flatMap((group) => group.views);

    if (!views?.length) {
      return null;
    }

    const firstView = views[0];
    return firstView?.id || null;
  }

  function getSegmentFirstGroupId(id: number) {
    const segment = getSegmentById(id);

    const groups = segment?.view_groups;

    if (!groups?.length) {
      return null;
    }

    const firstValidGroup = SafeNavigation.findFirstValidGroup(groups);

    if (firstValidGroup) {
      const firstView = SafeNavigation.getFirstView(firstValidGroup);
      if (firstView) {
        return { groupId: firstValidGroup.id, viewId: firstView.id };
      }
    }

    return null;
  }

  return {
    currentSegments,
    requiredSegments,
    unrequiredSegments,
    validRequiredSegments,
    getSegmentById,
    getSegmentNameById,
    getSegmentFirstViewId,
    getSegmentFirstGroupId,
  };
}
